import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { useContext } from 'react';
import { useQuery } from 'react-query';
import AppBarContext from '../../../components/BusinessPortalAppBarContext';
import { useDashboard } from '../../Dashboard/DashboardContext';
import DetailsCard from './DetailsCard';

const PREFIX = 'KPIDetailsCard';

const classes = {
  details: `${PREFIX}-details`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.details}`]: {
    position: 'relative',
    width: '100%',
    padding: theme.spacing(1)
  }
}));

interface KPIDetailsCardItem {
  title?: string;
  actualLabel?: string;
  targetLabel?: string;
  periodLabel?: string;
  meaId?: string;
  targetSeries?: string;
  actualSeries?: string;
}

interface Data {
  name?: string;
  rangeColor?: string;
  prettyActual?: string;
  prettyTarget?: string;
}

const KPIDetailsCard = ({
  title = 'Details',
  actualLabel = 'Actual',
  targetLabel = 'Target',
  periodLabel = 'Period',
  meaId,
  targetSeries = 'TAR',
  actualSeries = 'ACT'
}: KPIDetailsCardItem) => {
  const [queryString] = useDashboard();
  const { setError } = useErrorContext();
  const { selectedPeriod, getPeriodName } = useContext(AppBarContext);

  const { isLoading, error, data, isFetched } = useQuery<
    AxiosResponse<Data[]>,
    AxiosError<ErrorObject>
  >(
    `detailscard-${queryString.get('meaId')}-${getPeriodName()}`,
    async () => {
      const response = await axios.post('meaquery', {
        query: `[${queryString.get('meaId') || meaId}]`,
        attributes: `name, prettyvalue(series="${targetSeries}", period="${getPeriodName()}", as="prettyTarget"), prettyvalue(series="${actualSeries}", period="${getPeriodName()}", as="prettyActual"), rangeColor(period="${getPeriodName()}")`
      });
      return response.data;
    },
    {
      // The query will not execute until the selectedPeriod exists
      enabled: !!selectedPeriod
    }
  );

  if (error && error.response) setError(error.response.data);

  if (!isLoading && !isFetched) return null;

  if (isLoading)
    return (
      <Root style={{ textAlign: 'center', marginTop: '32px' }}>
        <CircularProgress />
      </Root>
    );

  return data ? (
    <DetailsCard
      title={title}
      actualLabel={actualLabel}
      targetLabel={targetLabel}
      periodLabel={periodLabel}
      data={data.data[0]}
      periodName={getPeriodName()}
    />
  ) : (
    <div className={classes.details} data-testid="KPIDetailsCard-no-data">
      No data found
    </div>
  );
};

export default KPIDetailsCard;
